import React, { useEffect, useState } from 'react';
import './Plans.css';
import { useUsd } from '../shared/signup/usd';

// The plans on sale with their live prices (set by the Super Admin in the admin panel, /api/plans). These are the
// default prices, shown when the site runs without the backend (npm start) or the request fails.
const DEFAULT_PLANS = [
  { key: 'trial', price: 0, trialDays: 1 },
  { key: 'monthly', price: 399, periodMonths: 1 },
  { key: 'yearly', price: 4499, periodMonths: 12 },
  { key: 'franchise', price: 299, periodMonths: 1, minQuantity: 20 },
];

// What each card says; the price comes from the plan. Every plan includes the same website, admin panel and setup;
// paid plans add your own domain and the storage/server charge (the plan's live price, see storageLine).
const BASE_FEATURES = [
  'Your shop website — free',
  'Admin panel — free',
  'Full store setup — free',
  'Safe & secure (SSL) — free',
  'Works on every phone',
  'Free subdomain (shopname.ownezzy.com)',
];
const OWN_DOMAIN = 'Main domain (shopname.com) — third-party fee / year';
const END_FEATURES = ['WhatsApp contact button', 'Product catalogue'];

const CARDS = {
  trial: {
    icon: 'bi-rocket-takeoff',
    tag: 'Start here',
    title: 'Free trial',
    text: 'Launch your store today, explore everything, then decide.',
  },
  monthly: {
    icon: 'bi-shop',
    tag: 'For your shop',
    title: 'Monthly',
    text: 'Keep your store online and growing, month by month.',
  },
  yearly: {
    icon: 'bi-calendar2-check',
    tag: 'Pay once a year',
    title: 'Yearly',
    text: 'One payment keeps your store running for the whole year.',
  },
  franchise: {
    icon: 'bi-people',
    tag: 'For partners',
    title: 'Franchise',
    text: 'For agencies and resellers who set up stores for many shops.',
  },
};

// money: the plan price shown to this visitor (useUsd's price: US$ outside India, ₹ in India)
function priceLine(plan, money) {
  if (plan.key === 'trial') return { price: money(0), unit: `for ${plan.trialDays || 1} day${plan.trialDays === 1 ? '' : 's'}`, hint: 'No payment needed' };
  if (plan.key === 'franchise') return { price: money(plan.price), unit: '/ website / month', hint: `Minimum ${plan.minQuantity || 20} websites` };
  if (plan.periodMonths === 12) return { price: money(plan.price), unit: '/ year', hint: 'Billed once a year' };
  // The first-month offer (Settings -> Plans): the offer price first, the usual price after it in small text
  if (plan.firstMonthOffer) return { price: money(plan.firstMonthOffer.price), unit: '/ first month', hint: `Then ${money(plan.price)}/month · new customers only`, offer: true };
  return { price: money(plan.price), unit: '/ month', hint: plan.price ? `About ${money(Math.ceil(plan.price / 30))} a day` : '' };
}

// The storage/server line uses the live price, so it always matches the price shown on the card
function storageLine(plan, money) {
  if (plan.key === 'franchise') return `Storage/server partner rate — ${money(plan.price)}/month (up to 2 GB storage per website)`;
  if (plan.periodMonths === 12) return `Storage/server charge — ${money(plan.price)}/year (up to 2 GB storage)`;
  return `Storage/server charge — ${money(plan.price)}/month (up to 2 GB storage)`;
}

// Every website's monthly emails (Settings -> Limits & add-ons): login codes, order confirmations and the like
const emailLine = (limits) => `${Number(limits.emailsPerMonth || 2000).toLocaleString('en-IN')} transactional emails per website every month`;

function featuresFor(plan, limits, money) {
  if (plan.key === 'trial') return [...BASE_FEATURES, emailLine(limits), ...END_FEATURES];
  const list = [...BASE_FEATURES, OWN_DOMAIN, storageLine(plan, money), emailLine(limits), ...END_FEATURES];
  return plan.key === 'franchise' ? ['Buy websites in bulk', ...list] : list;
}

export default function Plans() {
  const [plans, setPlans] = useState(DEFAULT_PLANS);
  const [limits, setLimits] = useState({ emailsPerMonth: 2000 });
  const { on: international, price: money, charged } = useUsd(); // plan prices in US$ for visitors outside India, charged in ₹

  useEffect(() => {
    let live = true;
    fetch('/api/plans')
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        const known = data && Array.isArray(data.plans) ? data.plans.filter((p) => CARDS[p.key]) : [];
        if (live && known.length) setPlans(known);
        if (live && data && data.limits) setLimits(data.limits);
      })
      .catch(() => {});
    return () => { live = false; };
  }, []);

  return (
    <section className="section-pad plans-section" id="plans">
      <div className="container">
        <div className="section-heading text-center">
          <span className="section-kicker">PRICING & PLANS • 0% COMMISSION</span>
          <h2>Get a free website for your shop with 0% commission</h2>
          <p>14-Day Free Trial ({money(0)}). Zero sales commission. Affordable plans starting at {money(plans.find((p) => p.key === 'monthly')?.price || 399)}/month, with your own custom domain on paid plans.</p>
        </div>
        <div className="pl-grid">
          {plans.map((plan) => {
            const card = CARDS[plan.key];
            const { price, unit, hint, offer } = priceLine(plan, money);
            return (
              <article className={`pl-card pl-${plan.key}${offer ? " pl-has-offer" : ""}`} key={plan.key}>
                <div className="pl-head">
                  <span className="pl-icon"><i className={`bi ${card.icon}`} aria-hidden="true" /></span>
                  {offer ? (
                    <span className="pl-tag pl-offer-tag"><i className="bi bi-gift-fill" aria-hidden="true" /> Limited time offer</span>
                  ) : <span className="pl-tag">{card.tag}</span>}
                </div>
                <h3>{card.title}</h3>
                <p className="pl-text">{card.text}</p>
                <div className="pl-price">
                  <strong>{price}</strong> <span>{unit}</span>
                </div>
                {plan.key !== 'trial' && charged(offer ? plan.firstMonthOffer.price : plan.price) && <div className="pl-usd">{charged(offer ? plan.firstMonthOffer.price : plan.price)}</div>}
                {hint && <div className="pl-hint">{hint}</div>}
                {plan.gstPercent > 0 && <div className="pl-gst">+ {plan.gstPercent}% GST</div>}
                <ul>
                  {featuresFor(plan, limits, money).map((feature) => (
                    <li key={feature}><i className="bi bi-check2" aria-hidden="true" /> {feature}</li>
                  ))}
                </ul>
              </article>
            );
          })}
        </div>
        <p className="plans-terms-note">
          By starting a free trial or any paid plan, you agree to Ownezzy's <a href="/terms">Terms of Service</a>, <a href="/privacy">Privacy Policy</a>, <a href="/refund-policy">Refund Policy</a> and <a href="/delivery-policy">Delivery Policy</a>.
          We don't charge for your website or its maintenance; plan fees cover hosting, storage and third-party costs such as domain names. Plan payments are not refundable, so start with the free trial if you are unsure.
          {' '}{international ? 'Prices are shown in US$ for visitors outside India. Payment is charged in Indian Rupees (₹) by card, and your bank converts it, so the US$ prices are estimates.' : 'All prices are in Indian Rupees (₹). We accept UPI and Indian and international cards; international cards are charged in rupees and your bank converts the amount.'}
        </p>
      </div>
    </section>
  );
}
